import React from 'react';
import { colors } from '../data/designTokens';
import styles from './ScreenPrimitives.module.scss';

// Fixed boilerplate report pages — the table of contents and the legal
// disclaimer. Both are data-light and rendered once by ReportPreview.

export type TocEntry = {
    index: string;
    title: string;
    subtitle?: string;
    dataSource?: string;
    coverage?: string[];
};

export const ScreenToc: React.FC<{ orgName: string; entries: TocEntry[] }> = ({ orgName, entries }) => (
    <div className={styles.ScreenPage}>
        <div className={styles.RunningHeader}>
            <span>{orgName}</span>
            <span>Contents</span>
        </div>

        <div style={{ marginTop: 16 }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: '0.75rem',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: colors.ink40,
                    fontWeight: 600,
                }}
            >
                <span>Report Structure</span>
            </div>
            <div
                style={{
                    fontSize: '1.8rem',
                    fontFamily: "'Fraunces', Georgia, serif",
                    marginTop: 4,
                    lineHeight: 1.15,
                    color: colors.ink00,
                    letterSpacing: '-0.01em',
                }}
            >
                Contents
            </div>
            <div
                style={{
                    fontSize: '0.85rem',
                    color: colors.ink20,
                    marginTop: 6,
                    lineHeight: 1.5,
                    maxWidth: '85%',
                }}
            >
                {entries.length} module{entries.length === 1 ? '' : 's'} compiled from Recur Club's credit intelligence pipeline.
            </div>
        </div>

        <div style={{ marginTop: 20, borderTop: `1px solid ${colors.ink10}` }}>
            {entries.map((e) => (
                <div
                    key={e.index}
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        padding: '9px 0',
                        borderBottom: `1px solid ${colors.ruleSoft}`,
                        gap: 14,
                    }}
                >
                    <div
                        style={{
                            width: 36,
                            flexShrink: 0,
                            fontSize: '0.92rem',
                            fontFamily: "'Geist Mono', monospace",
                            color: colors.ink40,
                            paddingTop: 2,
                        }}
                    >
                        {e.index}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                            style={{
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                color: colors.ink00,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                            }}
                        >
                            {e.title}
                        </div>
                        {e.subtitle ? (
                            <div
                                style={{
                                    fontSize: '0.78rem',
                                    color: colors.ink40,
                                    marginTop: 4,
                                    lineHeight: 1.45,
                                }}
                            >
                                {e.subtitle}
                            </div>
                        ) : null}
                    </div>
                </div>
            ))}
        </div>

        <div className={styles.RunningFooter}>
            <span>AICA Report &mdash; Confidential</span>
            <span>Contents</span>
        </div>
    </div>
);

const DISCLAIMER_PARAGRAPHS = [
    'This Risk Assessment Report ("Report") has been prepared by ("Founderlink Technologies Pvt Ltd ("Company")") solely for informational purposes of the Company\'s client ("Client") which is registered on its proprietary AI credit and monitoring assessment platform ("AICA"). This Report is intended to assist in assessing potential risks associated with the Client\'s third-party engagements and should not be considered exhaustive or definitive. The Company\'s work including this Report is solely for the Client and is not to be relied upon or shared with any other person or third party without our prior written permission.',
    'Limited Scope: The findings, analyses, and recommendations presented in this Report are based on information available to the Company, either through publicly available information or through information provided by the third party being assessed as part of the Client\'s engagements ("Client\'s Customer"), at the time of its preparation. The Company has made reasonable efforts to ensure the accuracy and completeness of the information contained herein, but makes no representations or warranties, express or implied, regarding the accuracy, reliability, or completeness of the Report. The Company has no responsibility to update this Report for events or circumstances occurring after the date of the Report, unless specifically requested for on mutually acceptable terms.',
    'Not Professional Advice: This Report does not constitute legal, accounting, audit, financial or any other specialist advice or a legal formal due diligence report. Our investigation is not performed in accordance with generally accepted auditing, review, and other assurance standards. It is not a substitute for independent professional judgment or analysis. Users of this Report As a permitted user of this Report, the Client is encouraged to seek appropriate professional advice tailored to their specific circumstances before making any decisions or taking any actions based on the information provided herein.',
    "Third-Party Information: The Company has relied on information obtained from third parties, including but not limited to publicly available sources, government databases and data provided by the third parties' Client's Customers themselves. While reasonable efforts have been made to verify the accuracy and reliability of such information, the Company does not assume any responsibility or liability for the accuracy, completeness, or reliability of third-party information.",
    'Assumptions and Limitations: The assessments and conclusions presented in this Report are based on certain assumptions, methodologies, and models. These may not accurately reflect all possible scenarios or outcomes. Users of this Report should be aware of the inherent limitations of the methodologies employed and exercise caution in their interpretation of the results.',
    'Limitation of Liability: In no event shall the Company (and any employee, officer, director, or affiliate of the Company) be liable for any direct, indirect, incidental, consequential, special, or punitive damages arising out of or in connection with the use of this Report, including but not limited to any action taken basis this report, lost profits, loss of business, or loss of data, even if advised of the possibility of such damages. The maximum liability of the Company for any claim arising out of or relating to this Report shall not exceed the amount paid by the user for the Report.',
    'Regulatory Compliance: Users of this Report are solely responsible for ensuring compliance with all applicable laws, regulations, and industry standards governing third-party engagements, risk management practices, and data protection requirements in India or any other jurisdiction relevant to their operations.',
    'Modification and Update: The Company reserves the right to modify or update this Report at any time without prior notice. It is the responsibility of users to review the most current version of the Report for any changes or updates.',
    'By accessing or using this Report, users acknowledge that they have read, understood, and agree to be bound by the terms and conditions set forth in this Disclaimer. If you do not agree with any part of this Disclaimer, you should refrain from accessing or using this Report.',
];

export const ScreenDisclaimer: React.FC = () => (
    <div style={{ padding: '32px 24px', maxWidth: 800 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.ink00, marginBottom: 16 }}>Disclaimer</h3>
        {DISCLAIMER_PARAGRAPHS.map((p, i) => (
            <p key={i} style={{ fontSize: 11, color: colors.ink20, lineHeight: 1.6, marginBottom: 8 }}>
                {p}
            </p>
        ))}
    </div>
);
